/** Fail-closed write gate — same server flag as corporate Client writes / E1 work. */
import { ukrainianDateTimeFromIso } from "./ukrainianDateEditor";

export function isCorporateWorkWritesAvailable(company) {
  return company?.corporate_clients_available === true;
}

export const TASK_STATUS_LABELS = {
  todo: "До виконання",
  in_progress: "В роботі",
  done: "Виконано",
  cancelled: "Скасовано",
};

export const VIEWING_STATUS_LABELS = {
  scheduled: "Заплановано",
  completed: "Завершено",
  cancelled: "Скасовано",
};

export const TASK_PRIORITY_LABELS = {
  low: "Низький",
  medium: "Середній",
  high: "Високий",
};

export const TASK_STATUS_OPTIONS = [
  { value: "todo", label: TASK_STATUS_LABELS.todo },
  { value: "in_progress", label: TASK_STATUS_LABELS.in_progress },
  { value: "done", label: TASK_STATUS_LABELS.done },
  { value: "cancelled", label: TASK_STATUS_LABELS.cancelled },
];

export const VIEWING_STATUS_OPTIONS = [
  { value: "scheduled", label: VIEWING_STATUS_LABELS.scheduled },
  { value: "completed", label: VIEWING_STATUS_LABELS.completed },
  { value: "cancelled", label: VIEWING_STATUS_LABELS.cancelled },
];

export const TASK_OPEN_STATUSES = new Set(["todo", "in_progress"]);
export const VIEWING_OPEN_STATUSES = new Set(["scheduled"]);
export const TASK_TERMINAL_STATUSES = new Set(["done", "cancelled"]);
export const VIEWING_TERMINAL_STATUSES = new Set(["completed", "cancelled"]);

export function taskStatusLabel(status) {
  return TASK_STATUS_LABELS[status] || status || "—";
}

export function viewingStatusLabel(status) {
  return VIEWING_STATUS_LABELS[status] || status || "—";
}

export function priorityLabel(priority) {
  return TASK_PRIORITY_LABELS[priority] || priority || "—";
}

export function workKindLabel(kind) {
  return kind === "viewing" ? "Перегляд" : "Задача";
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  const formatted = ukrainianDateTimeFromIso(iso);
  return formatted || String(iso);
}

/**
 * Terminal and cancelled rows are never overdue.
 * Additive personal compatibility: cancelled excluded here too.
 */
export function isWorkOverdue(item, kind = "task") {
  if (!item) return false;
  if (kind === "viewing") return false;
  if (TASK_TERMINAL_STATUSES.has(item.status)) return false;
  if (!item.due_date) return false;
  const due = new Date(item.due_date);
  if (Number.isNaN(due.getTime())) return false;
  return due.getTime() < Date.now();
}

export function linkedClientLabel(linked) {
  if (!linked) return "Без клієнта";
  if (linked.unavailable) return "Клієнт недоступний";
  const parts = [linked.first_name, linked.last_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (linked.id != null) return `Клієнт #${linked.id}`;
  return "Клієнт";
}

export function linkedPropertyLabel(linked) {
  if (!linked) return "Без об'єкта";
  if (linked.unavailable) return "Об'єкт недоступний";
  if (linked.title) return linked.title;
  if (linked.id != null) return `Об'єкт #${linked.id}`;
  return "Об'єкт";
}

export function isLinkedAccessible(linked) {
  return Boolean(linked && !linked.unavailable && linked.id != null);
}

export function membershipGenerationOf(membership) {
  if (membership == null) return null;
  return membership.membership_generation ?? null;
}

export function canMutateWithGate(company, capabilities) {
  if (!isCorporateWorkWritesAvailable(company)) return false;
  if (!capabilities) return false;
  return Boolean(
    capabilities.can_mutate_open ||
      capabilities.can_assign ||
      capabilities.can_relink ||
      capabilities.can_reopen ||
      capabilities.can_delete,
  );
}

export function outcomeHistoryEntries(work) {
  const raw = work?.outcome_history;
  return Array.isArray(raw) ? raw : [];
}

const OUTCOME_KIND_LABELS = {
  complete: "Завершення",
  cancel: "Скасування",
  reopen: "Повторне відкриття",
};

export function outcomeKindLabel(kind) {
  return OUTCOME_KIND_LABELS[kind] || kind || "—";
}

export function formatOutcomeHistoryEntry(entry, kind = "task") {
  if (!entry || typeof entry !== "object") return "—";
  const statusFn = kind === "viewing" ? viewingStatusLabel : taskStatusLabel;
  const fromLabel = statusFn(entry.from_status);
  const toLabel = statusFn(entry.to_status);
  const kindLabel = outcomeKindLabel(entry.kind);
  const summary = entry.result_summary
    ? String(entry.result_summary).trim()
    : "";
  const when = formatDateTime(entry.at);
  const transition = `${fromLabel} → ${toLabel}`;
  return summary
    ? `${when} · ${kindLabel} · ${transition} — ${summary}`
    : `${when} · ${kindLabel} · ${transition}`;
}

export function memberDisplayName(member) {
  if (!member) return null;
  const user = member.user || {};
  const userId = user.id ?? member.user_id;
  const name =
    user.fullname ||
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    "";
  if (name.trim()) return name.trim();
  if (userId != null) return `Учасник #${userId}`;
  return null;
}

export function executorLabel(userId, members = []) {
  if (userId == null) return "Непризначено";
  const match = (members || []).find((m) => {
    const id = m?.user?.id ?? m?.user_id;
    return id != null && String(id) === String(userId);
  });
  const named = memberDisplayName(match);
  if (named) return named;
  return `Учасник #${userId}`;
}

export function messageFromWorkError(err) {
  const data = err?.response?.data;
  if (data?.code === "outcome_history_limit") {
    return "Досягнуто ліміт історії результатів (50). Зверніться до адміністратора.";
  }
  if (
    data?.code === "assignment_revision_conflict" ||
    data?.code === "membership_generation_conflict" ||
    err?.response?.status === 409
  ) {
    return (
      data?.detail ||
      "Конфлікт версії. Оновіть дані та повторіть навмисно."
    );
  }
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (err?.message) return err.message;
  return "Помилка запиту";
}
